import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
	type ReactNode,
} from "react"
import {
	Alert,
	Box,
	BoxProps,
	Button,
	Divider,
	Dropdown,
	Heading,
	Icons,
	Inline,
	Input,
	Stack,
	Text,
	noSpinnersClassName,
} from "@sembark-travel/ui-base"
import {
	ImageCropInputField,
	DatePickerField,
	ErrorMessage,
	FieldArray,
	FieldGroup,
	Form,
	GetFieldValue,
	PhoneInputField,
	SelectField,
	SelectInputField,
	SubmissionError,
	TextInputField,
	arrayMutators,
	useFieldValue,
	useForm,
	useFormState,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import {
	NavigationPromptDialog,
	useNavigationPrompt,
} from "@sembark-travel/ui-router"
import { parseDate } from "@sembark-travel/datetime-utils"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { useXHR } from "@sembark-travel/xhr"
import config from "../config"
import { useAuthUser } from "../Auth"
import { AmenitiesField, type TAmenity } from "../Amenities"
import {
	ChannelManagerSelect,
	type TChannelManagerOption,
} from "../ChannelManagers"
import {
	SelectTenantCurrencyInputField,
	useFunctionalCurrencyOfTenant,
} from "../Currencies"
import {
	OtaMealPlanInDialog,
	OtaMealPlansTable,
	type TOtaMealPlanPayload,
} from "../HotelOtaMealPlans"
import {
	OTA_BASE_CHILD_OCCUPANCY,
	OtaRoomTypeInDialog,
	OtaRoomTypesTable,
	type IOtaRoomTypeFormSchema,
} from "../HotelOtaRoomTypes"
import {
	type ICity,
	type ICountryState,
	type ILocation,
	SelectCities,
	SelectCountries,
	SelectStates,
} from "../Locations"
import {
	SelectTripDestination,
	type TTripDestination,
} from "../TripDestinations"
import { COLUMN_PILL_COLORS, columnPillVariant } from "../RateSheets/gridStyles"
import { SelectHotelLocation } from "./Locations"
import {
	hotelsXHR,
	type IHotel,
	type THotelLocation,
	type THotelOtaChildAgePaxType,
	type THotelRemotePaxTypes,
	useHotelRemotePaxTypes,
} from "./store"
import {
	bandRemotePaxType,
	bandRemotePaxTypeField,
	bandStartAges,
	buildNewHotelFormData,
	CHILD_AGE_BAND_OCCUPANCIES,
	HOTEL_STAR_OPTIONS,
	isWholeNumber,
	newHotelStepSchemas,
	withUrlProtocol,
	occupancyPaxTypes,
	otaChildAgesToFormValues,
	withoutChildAgeRemotePaxTypes,
} from "./utils"
import { acceptedImageTypes } from "../shared/utils"

export type TChildAgeBandOccupancy = "CNB" | "CWEB" | "CNB_CWEB"

// Start ages are never part of the form: the first band starts at `min_age`
// and every next one right after the previous band's end (see `bandStartAges`).
export type TChildAgeBand = {
	end_age?: number
	occupancy?: TChildAgeBandOccupancy
	// the channel manager's pax type this band is sold as, see `bandRemotePaxType`
	cnb_remote_pax_type?: string
	cweb_remote_pax_type?: string
}

export type TChildAgesFormValues = {
	min_age?: number
	max_age?: number
	aweb_remote_pax_type?: string
	bands: Array<TChildAgeBand>
}

export type TNewHotelFormValues = {
	// Step 1: Basic details
	name: string
	stars?: string
	currency: string
	location?: THotelLocation
	trip_destinations?: Array<TTripDestination>
	brand_code: string
	image?: File
	address: {
		line_1: string
		line_2: string
		landmark: string
		postcode: string
		city?: ILocation["city"]
		state?: ILocation["state"]
		country?: ILocation["country"]
	}
	checkin_at: string | Date
	checkout_at: string | Date
	contacts: Array<{
		name: string
		email?: string
		phone_numbers: Array<{
			country_code: string
			phone_number: string
			number: string
			is_primary?: 1 | 0
		}>
	}>
	maps_url?: string
	amenities: Array<TAmenity>
	// Step 2
	ota_room_types: Array<IOtaRoomTypeFormSchema>
	// Step 3
	ota_meal_plans: Array<TOtaMealPlanPayload>
	// Step 4
	channel_manager?: TChannelManagerOption | null
	// Step 5
	child_ages: TChildAgesFormValues
}

const NEW_HOTEL_STEPS = [
	"Basic details",
	"Room Type",
	"Meal Plans",
	"Connect Channel Manager",
	"Child Ages",
] as const

function getInitialValues({
	currency,
	withTripDestinations,
}: {
	currency: string
	withTripDestinations: boolean
}): TNewHotelFormValues {
	return {
		name: "",
		stars: undefined,
		currency,
		location: undefined,
		trip_destinations: withTripDestinations ? [] : undefined,
		brand_code: "",
		image: undefined,
		address: {
			line_1: "",
			line_2: "",
			landmark: "",
			postcode: "",
			city: undefined,
			state: undefined,
			country: undefined,
		},
		checkin_at: parseDate(config.defaultCheckinAt, config.timeFormat),
		checkout_at: parseDate(config.defaultCheckoutAt, config.timeFormat),
		contacts: [
			{
				name: "",
				email: "",
				phone_numbers: [
					{
						country_code: "IN",
						phone_number: "+91",
						number: "",
						is_primary: 1,
					},
				],
			},
		],
		maps_url: "",
		amenities: [],
		ota_room_types: [],
		ota_meal_plans: [],
		child_ages: otaChildAgesToFormValues(undefined),
		channel_manager: undefined,
	}
}

// Fields shared by the create hotel form and the edit hotel form
export type THotelDetailsFormValues = Pick<
	TNewHotelFormValues,
	| "name"
	| "stars"
	| "currency"
	| "location"
	| "trip_destinations"
	| "brand_code"
	| "image"
	| "address"
	| "checkin_at"
	| "checkout_at"
	| "amenities"
	| "maps_url"
>

export function CreateHotel({
	onSuccess,
	onCancel,
}: {
	onSuccess: (hotel: IHotel) => void
	onCancel: () => void
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const canAttachTripDestinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)
	const functionalCurrency = useFunctionalCurrencyOfTenant()
	const [step, setStep] = useState(0)
	const { blocker, enablePrompt, disablePrompt } = useNavigationPrompt(false)
	const [initialValues] = useState(() =>
		getInitialValues({
			currency: functionalCurrency.currency,
			withTripDestinations: canAttachTripDestinations,
		})
	)
	const { stepValidators, validate } = useMemo(() => {
		const schemas = newHotelStepSchemas({
			requireTripDestinations: canAttachTripDestinations,
		})
		return {
			stepValidators: schemas.steps.map((schema) => validateFormValues(schema)),
			validate: validateFormValues(schemas.all),
		}
	}, [canAttachTripDestinations])
	const isLastStep = step === NEW_HOTEL_STEPS.length - 1
	// the channel manager's pax types, loaded by the child ages step
	const remotePaxTypesRef = useRef<THotelRemotePaxTypes>(undefined)

	return (
		<Form<TNewHotelFormValues>
			initialValues={initialValues}
			validate={validate}
			mutators={{ ...arrayMutators }}
			onSubmit={withServerErrors(async (values) => {
				try {
					// the mappings are stored against the channel manager's defaults
					const hotel = await hotelsXHR(xhr).createHotel(
						buildNewHotelFormData(values, remotePaxTypesRef.current)
					)
					disablePrompt()
					showSnackbar("Hotel added successfully")
					onSuccess(hotel)
				} catch (e) {
					// errors can belong to fields of the previous steps, which are hidden
					showSnackbar(
						(e as Error).message || "Something went wrong. Please try later."
					)
					throw e
				}
			})}
			subscription={{ submitting: true }}
		>
			{({ form, submitting }) => {
				async function goNext() {
					// only the current step's fields must be valid to move ahead
					const errors = await stepValidators[step](form.getState().values)
					if (errors) {
						// touch the step's fields, so their errors show up
						form
							.getRegisteredFields()
							.forEach((name) => form.blur(name as keyof TNewHotelFormValues))
						return
					}
					// the brand code is checked against the server while typing, and its
					// field is gone once we move on, so a taken code must stop us here
					const brandCode = form.getFieldState("brand_code")
					if (step === 0 && (brandCode?.error || brandCode?.validating)) {
						form.blur("brand_code")
						return
					}
					if (isLastStep) {
						await form.submit()
						return
					}
					setStep(step + 1)
					window.scrollTo(0, 0)
				}
				function goBack() {
					if (step === 0) {
						onCancel()
						return
					}
					setStep(step - 1)
					window.scrollTo(0, 0)
				}
				// the first destination also pre-fills the child ages of this form
				function changeChildAges(destination: TTripDestination) {
					form.change("child_ages", {
						...form.getState().values.child_ages,
						min_age: destination.min_child_age,
						max_age: destination.max_child_age,
					})
				}
				return (
					<form
						noValidate
						onSubmit={(e) => {
							e.preventDefault()
							goNext()
						}}
					>
						<PromptWhenDirty
							enablePrompt={enablePrompt}
							disablePrompt={disablePrompt}
						/>
						<NavigationPromptDialog
							blocker={blocker}
							title="You have unsaved hotel details. Are you sure you want to leave? All changes will be lost."
						/>
						<StepsHeader
							onBack={goBack}
							backTitle={step === 0 ? "Back to hotels" : "Previous step"}
							actionButton={
								<Button type="submit" level="primary" disabled={submitting}>
									{submitting ? <Icons.Spinner /> : null}{" "}
									{isLastStep
										? submitting
											? "Adding hotel..."
											: "Add hotel"
										: "Next"}
									<Icons.ChevronDown rotate="270" />
								</Button>
							}
						/>
						<Inline alignItems="stretch" gap="0" collapseBelow="md">
							<StepsSidebar currentStep={step} />
							<Stack
								flex="1"
								minWidth="0"
								gap="4"
								paddingX={{ xs: "4", md: "8" }}
								paddingY="6"
							>
								<Box display={{ xs: "block", md: "none" }}>
									<Text fontSize="sm" color="muted">
										Step {step + 1} of {NEW_HOTEL_STEPS.length}:{" "}
										{NEW_HOTEL_STEPS[step]}
									</Text>
								</Box>
								{step === 0 ? (
									<BasicDetailsStep
										withTripDestinations={canAttachTripDestinations}
										onFirstDestinationChange={changeChildAges}
									/>
								) : step === 1 ? (
									<GetFieldValue<
										Array<IOtaRoomTypeFormSchema> | undefined
									> name="ota_room_types">
										{({ value, onChange }) => (
											<RoomTypesStep
												roomTypes={value || []}
												onChange={onChange}
											/>
										)}
									</GetFieldValue>
								) : step === 2 ? (
									<GetFieldValue<
										Array<TOtaMealPlanPayload> | undefined
									> name="ota_meal_plans">
										{({ value, onChange }) => (
											<MealPlansStep
												mealPlans={value || []}
												onChange={onChange}
											/>
										)}
									</GetFieldValue>
								) : step === 3 ? (
									<ChannelManagerStep name="channel_manager" />
								) : (
									<ChildAgesStep
										name="child_ages"
										onRemotePaxTypes={(remotePaxTypes) => {
											remotePaxTypesRef.current = remotePaxTypes
										}}
									/>
								)}
								{step === 1 ? (
									<ErrorMessage name="ota_room_types" />
								) : step === 2 ? (
									<ErrorMessage name="ota_meal_plans" />
								) : null}
								{isLastStep ? <SubmissionError /> : null}
							</Stack>
						</Inline>
					</form>
				)
			}}
		</Form>
	)
}

function PromptWhenDirty({
	enablePrompt,
	disablePrompt,
}: {
	enablePrompt: () => void
	disablePrompt: () => void
}) {
	const { dirty, submitSucceeded } = useFormState({
		subscription: { dirty: true, submitSucceeded: true },
	})
	useEffect(() => {
		if (dirty && !submitSucceeded) {
			enablePrompt()
		} else {
			disablePrompt()
		}
	}, [dirty, submitSucceeded, enablePrompt, disablePrompt])
	return null
}

function StepsHeader({
	onBack,
	backTitle,
	actionButton,
}: {
	onBack: () => void
	backTitle: string
	actionButton: React.ReactNode
}) {
	return (
		<Inline
			gap="4"
			justifyContent="between"
			alignItems="center"
			bgColor="inset"
			paddingY="3"
			paddingX={{ xs: "4", md: "8" }}
			borderBottomWidth="1"
		>
			<Inline gap="3" alignItems="center">
				<Box onClick={onBack} title={backTitle} cursor="pointer">
					<Icons.ArrowLeft />
				</Box>
				<Heading as="h1" fontSize="xl">
					Add hotel
				</Heading>
			</Inline>
			{actionButton}
		</Inline>
	)
}

// Only shows the progress. Steps are changed with the back and next buttons.
function StepsSidebar({ currentStep }: { currentStep: number }) {
	return (
		<Box
			display={{ xs: "none", md: "block" }}
			borderRightWidth="1"
			paddingX="8"
			paddingY="6"
			style={{ width: "18rem", flexShrink: 0 }}
		>
			<Stack as="ol" gap="0">
				{NEW_HOTEL_STEPS.map((title, index) => {
					const status =
						index < currentStep
							? "done"
							: index === currentStep
								? "current"
								: "pending"
					const isLast = index === NEW_HOTEL_STEPS.length - 1
					return (
						<Inline
							as="li"
							key={title}
							gap="3"
							alignItems="start"
							aria-current={status === "current" ? "step" : undefined}
						>
							<Stack alignItems="center" gap="0">
								<StepMarker status={status} />
								{isLast ? null : (
									<Box
										color={index < currentStep ? "primary" : "muted"}
										style={{
											height: "2.5rem",
											borderLeft: `1px ${
												index < currentStep ? "solid" : "dashed"
											} currentColor`,
										}}
									/>
								)}
							</Stack>
							<Stack gap="0">
								<Text fontSize="xs" color="muted">
									Step {index + 1}
								</Text>
								<Text
									fontWeight="semibold"
									color={status === "pending" ? "default" : "primary"}
								>
									{title}
								</Text>
							</Stack>
						</Inline>
					)
				})}
			</Stack>
		</Box>
	)
}

function StepMarker({ status }: { status: "done" | "current" | "pending" }) {
	if (status === "done") {
		return (
			<Box
				size="6"
				rounded="full"
				bgColor="primary_emphasis"
				color="on_emphasis"
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexShrink="0"
			>
				<Icons.Ok size="4" />
			</Box>
		)
	}
	if (status === "current") {
		return (
			<Box
				size="6"
				rounded="full"
				borderWidth="2"
				borderColor="primary_emphasis"
				display="flex"
				alignItems="center"
				justifyContent="center"
				flexShrink="0"
			>
				<Box size="2" rounded="full" bgColor="primary_emphasis" />
			</Box>
		)
	}
	return (
		<Box
			size="6"
			rounded="full"
			borderWidth="1"
			borderColor="emphasis"
			flexShrink="0"
			style={{ borderStyle: "dashed" }}
		/>
	)
}

/* Step 1: Basic details */

function BasicDetailsStep({
	withTripDestinations,
	onFirstDestinationChange,
}: {
	withTripDestinations: boolean
	onFirstDestinationChange: (destination: TTripDestination) => void
}) {
	return (
		<Stack gap="6">
			<StepCard>
				<Inline gap="8" alignItems="start" collapseBelow="md">
					<Stack gap="8" flex="1" minWidth="0">
						<HotelDetailsFields
							withTripDestinations={withTripDestinations}
							onFirstDestinationChange={onFirstDestinationChange}
						/>
						<Divider marginY="0" />
						<AddressFields />
						<Divider marginY="0" />
						<CheckinCheckoutFields />
						<Divider marginY="0" />
						<StepSection
							title="Contact details"
							description="Provide the details of the hotel's contact person."
						>
							<Inline gap="4" flexWrap="wrap">
								<TextInputField
									label="Name"
									name="contacts[0].name"
									type="text"
									placeholder="e.g. Contact person name"
								/>
								<PhoneInputField
									name="contacts[0].phone_numbers"
									label="Contact Number(s)"
									multi
								/>
								<TextInputField
									label="Email"
									name="contacts[0].email"
									type="email"
									placeholder="contact@example.com"
								/>
							</Inline>
						</StepSection>
					</Stack>
					<ImageCropInputField
						preview
						crop={{ aspect: 3 / 2, minHeight: 100 }}
						name="image"
						label="Hotel Image"
						accept={acceptedImageTypes.join(",")}
					/>
				</Inline>
			</StepCard>
			<StepCard>
				<StepSection
					title="Add Amenities"
					description="Select one or more hotel Amenities"
				>
					<AmenitiesField name="amenities" type="hotel" />
				</StepSection>
			</StepCard>
		</Stack>
	)
}

export function HotelDetailsFields({
	withTripDestinations,
	onFirstDestinationChange,
	hotelId,
}: {
	withTripDestinations: boolean
	// the hotel being edited, so its own brand code isn't reported as taken
	hotelId?: number
	// called with the destination the check-in/out timings were taken from, for
	// the forms that pre-fill more of their own fields from it
	onFirstDestinationChange?: (destination: TTripDestination) => void
}) {
	const form = useForm<THotelDetailsFormValues>()
	const xhr = useXHR()
	const validateBrandCode = useCallback(
		async (value: unknown) => {
			const brandCode = value as string
			// format errors are the validation schema's job, the api only accepts this shape
			if (!brandCode || !/^[A-Za-z0-9]{1,4}$/.test(brandCode)) return undefined
			try {
				const { is_available } = await hotelsXHR(
					xhr
				).checkBrandCodeAvailability(brandCode, hotelId)
				return is_available
					? undefined
					: `Brand code ${brandCode} is already in use`
			} catch {
				// the backend still enforces uniqueness on submit
				return undefined
			}
		},
		[xhr, hotelId]
	)
	function changeTripDestinations(
		destinations: Array<TTripDestination> | undefined
	) {
		form.batch(() => {
			form.change("trip_destinations", destinations || [])
			const [destination] = destinations || []
			if (!destination) return
			// the first destination pre-fills the hotel's timings
			if (destination.hotel_checkin_at) {
				form.change(
					"checkin_at",
					parseDate(destination.hotel_checkin_at, "HH:mm")
				)
			}
			if (destination.hotel_checkout_at) {
				form.change(
					"checkout_at",
					parseDate(destination.hotel_checkout_at, "HH:mm")
				)
			}
			onFirstDestinationChange?.(destination)
		})
	}
	return (
		<StepSection
			title="Basic Details"
			description="Provide basic details e.g. name, star rating and address."
		>
			<Inline gap="4" flexWrap="wrap">
				<TextInputField
					label="Hotel Name"
					name="name"
					type="text"
					placeholder="e.g. Hotel name"
				/>
				<SelectInputField label="Stars" name="stars" required>
					<option value="" disabled>
						Select stars
					</option>
					{HOTEL_STAR_OPTIONS.map((stars) => (
						<option key={stars} value={stars}>
							{stars}
						</option>
					))}
				</SelectInputField>
				<SelectTenantCurrencyInputField name="currency" label="Currency" />
				<SelectField
					label="Location"
					placeholder="e.g. City/State"
					name="location"
					select={SelectHotelLocation}
					creatable
				/>
				{withTripDestinations ? (
					<SelectField
						select={SelectTripDestination}
						name="trip_destinations"
						label="Trip Destination"
						multiple
						fetchOnMount
						onChange={changeTripDestinations}
					/>
				) : null}
				<TextInputField
					label="Brand Code"
					name="brand_code"
					placeholder="e.g. TAJ"
					type="text"
					help="Used in voucher numbers"
					required
					asyncValidate={validateBrandCode}
					asyncValidateWait={500}
					validatingText="Checking availability"
					onChange={(e) =>
						form.change("brand_code", e.currentTarget.value.toUpperCase())
					}
					style={{ maxWidth: "200px" }}
				/>
			</Inline>
		</StepSection>
	)
}

export function CheckinCheckoutFields() {
	return (
		<StepSection
			title="Configuration"
			description="Please provide the checkin/checkout time"
		>
			<Inline gap="4" flexWrap="wrap">
				<DatePickerField
					label="Check-in time"
					name="checkin_at"
					dateFormat={false}
					timeFormat="HH:mm"
					required
				/>
				<DatePickerField
					label="Check-out time"
					name="checkout_at"
					dateFormat={false}
					timeFormat="HH:mm"
					required
				/>
			</Inline>
		</StepSection>
	)
}

function UrlInputField({
	label,
	name,
	secondaryLabel,
	placeholder,
	help,
	disabled,
	...props
}: Omit<React.ComponentProps<typeof FieldGroup>, "children"> &
	Omit<React.ComponentProps<typeof Input>, "label">) {
	return (
		<FieldGroup<string>
			name={name}
			label={label}
			secondaryLabel={secondaryLabel}
			help={help}
			{...props}
		>
			{({ input, meta, ...otherProps }) => {
				const href =
					typeof input.value === "string"
						? withUrlProtocol(input.value) || undefined
						: undefined
				const isDisabled = disabled || otherProps.disabled

				return (
					<Box position="relative" width="full">
						<Input
							type="text"
							placeholder={placeholder}
							disabled={isDisabled}
							paddingRight="12"
							{...input}
							{...otherProps}
						/>
						{href && !isDisabled ? (
							<Box
								as="a"
								href={href}
								target="_blank"
								rel="noopener noreferrer"
								position="absolute"
								display="flex"
								alignItems="center"
								justifyContent="center"
								width="10"
								bgColor={{ default: "subtle", hover: "gray2" }}
								borderLeftWidth="1"
								borderColor="emphasis"
								color={{ default: "muted", hover: "default" }}
								title="Open link in new tab"
								aria-label="Open link in new tab"
								style={{
									top: "1px",
									right: "1px",
									bottom: "1px",
									borderTopRightRadius: "calc(0.3rem - 1px)",
									borderBottomRightRadius: "calc(0.3rem - 1px)",
								}}
							>
								<Icons.ArrowTopRightOnSquare />
							</Box>
						) : (
							<Box
								as="span"
								position="absolute"
								display="flex"
								alignItems="center"
								justifyContent="center"
								width="10"
								bgColor="subtle"
								borderLeftWidth="1"
								borderColor="emphasis"
								color="muted"
								cursor={isDisabled ? "disabled" : "default"}
								opacity={isDisabled ? "70" : undefined}
								aria-hidden="true"
								style={{
									top: "1px",
									right: "1px",
									bottom: "1px",
									borderTopRightRadius: "calc(0.3rem - 1px)",
									borderBottomRightRadius: "calc(0.3rem - 1px)",
								}}
							>
								<Icons.ArrowTopRightOnSquare />
							</Box>
						)}
					</Box>
				)
			}}
		</FieldGroup>
	)
}

export function AddressFields() {
	const form = useForm<THotelDetailsFormValues>()
	// picking a city/state fills the bigger areas it belongs to
	function changeCity(city: ICity | undefined) {
		const { address } = form.getState().values
		form.change("address", {
			...address,
			city,
			state: city?.state || address.state,
			country: city?.state?.country || address.country,
		})
	}
	function changeState(state: ICountryState | undefined) {
		const { address } = form.getState().values
		form.change("address", {
			...address,
			state,
			country: state?.country || address.country,
		})
	}
	return (
		<StepSection
			title="Address"
			description="Please provide the full address details of the hotel."
		>
			<Inline gap="4" flexWrap="wrap">
				<TextInputField
					label="Street Address"
					name="address.line_1"
					type="text"
					placeholder="Flat / House No. / Floor / Building"
					autoComplete="no_please"
				/>
				<TextInputField
					label="Locality/Area"
					name="address.line_2"
					type="text"
					placeholder="Colony / Street / Locality"
					autoComplete="no_please"
				/>
				<TextInputField
					label="Landmark"
					secondaryLabel="Optional"
					name="address.landmark"
					type="text"
					placeholder="e.g. Behind Cinema"
				/>
				<SelectField
					select={SelectCities}
					name="address.city"
					label="City/Town/District"
					placeholder="Type to search..."
					multiple={false}
					creatable
					onChange={changeCity}
				/>
				<SelectField
					select={SelectStates}
					name="address.state"
					label="State/Province/Region"
					placeholder="Type to search..."
					multiple={false}
					creatable
					onChange={changeState}
				/>
				<TextInputField
					label="Pincode"
					name="address.postcode"
					type="text"
					placeholder="e.g. Area Code"
					autoComplete="no_please"
				/>
				<SelectField
					select={SelectCountries}
					name="address.country"
					label="Country"
					placeholder="Type to search..."
					multiple={false}
				/>
			</Inline>
			<Box maxWidth="lg">
				<UrlInputField
					label="Google Maps Link"
					name="maps_url"
					secondaryLabel="Optional"
					type="text"
					placeholder="Paste Google Maps URL"
					help="Add Google Maps link for customers, to easily locate hotel"
				/>
			</Box>
		</StepSection>
	)
}

/* Shared by the room types and meal plans steps: the list with its add button, or an empty state */

function StepList({
	title,
	itemLabel,
	addLabel,
	icon,
	isEmpty,
	onAdd,
	children,
}: {
	title: string
	itemLabel: string
	addLabel: string
	icon: React.ReactNode
	isEmpty: boolean
	onAdd: () => void
	children: React.ReactNode
}) {
	if (isEmpty) {
		return (
			<Stack gap="16">
				<Heading as="h2" fontSize="xl">
					Add {itemLabel}
				</Heading>
				<Stack gap="4" justifyContent="center" alignItems="center">
					<Stack alignItems="center">
						{icon}
						<Text fontSize="lg" color="primary" fontWeight="semibold">
							Add {itemLabel} to continue
						</Text>
					</Stack>
					<Box>
						<Button type="button" level="primary" onClick={onAdd}>
							<Icons.Plus />
							Add {itemLabel}
						</Button>
					</Box>
				</Stack>
			</Stack>
		)
	}
	return (
		<Stack gap="4">
			<Inline gap="4" justifyContent="between" alignItems="center">
				<Heading as="h2" fontSize="xl">
					{title}
				</Heading>
				<Button type="button" level="primary" onClick={onAdd}>
					{addLabel}
				</Button>
			</Inline>
			{children}
		</Stack>
	)
}

function RowActionsMenu({
	itemType,
	itemName,
	onEdit,
	onDelete,
}: {
	itemType: string
	itemName: string
	onEdit: () => void
	onDelete: () => void | Promise<void>
}) {
	return (
		<Inline gap="2" justifyContent="end">
			<Button
				size="sm"
				level="tertiary"
				onClick={onEdit}
				title={`Edit ${itemType}`}
			>
				<Icons.Pencil />
			</Button>
			<ConfirmationDialogProvider>
				{({ confirm }) => (
					<>
						<Dropdown alignRight>
							<Dropdown.ToggleButton size="sm" level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<Dropdown.MenuItem
									color="danger"
									onClick={() => {
										confirm().then((yes) => {
											if (!yes) return
											return onDelete()
										})
									}}
								>
									<Icons.Ban /> Delete {itemType}
								</Dropdown.MenuItem>
							</Dropdown.Menu>
						</Dropdown>
						<ConfirmationDialog
							title={`Delete ${itemType}`}
							destructiveAction
							acceptActionLabel="Yes, remove"
						>
							<Alert status="error">
								Are you sure you want to Delete{" "}
								<Text as="span" fontWeight="semibold">
									{itemName}
								</Text>
								?
							</Alert>
						</ConfirmationDialog>
					</>
				)}
			</ConfirmationDialogProvider>
		</Inline>
	)
}

/* Step 2: Room types, only kept in the form until the hotel is added */

function RoomTypesStep({
	roomTypes,
	onChange,
}: {
	roomTypes: Array<IOtaRoomTypeFormSchema>
	onChange: (roomTypes: Array<IOtaRoomTypeFormSchema>) => void
}) {
	// `index` is set when editing an added room type
	const [dialog, setDialog] = useState<{ index?: number } | null>(null)
	const imageUrls = useImageDataUrls(
		useMemo(() => roomTypes.map((roomType) => roomType.image), [roomTypes])
	)
	const editingIndex = dialog?.index
	return (
		<Stack gap="4">
			<StepList
				title="Room Type"
				itemLabel="Room Type"
				addLabel="Add Room"
				icon={<Icons.BedDouble size="32" color="subtle" />}
				isEmpty={!roomTypes.length}
				onAdd={() => setDialog({})}
			>
				<OtaRoomTypesTable
					otaRoomTypes={roomTypes.map((roomType, index) => ({
						name: roomType.name,
						base_adult_occupancy: Number(roomType.base_adult_occupancy),
						base_child_occupancy: OTA_BASE_CHILD_OCCUPANCY,
						max_occupancy: Number(roomType.max_occupancy),
						max_adult_occupancy: Number(roomType.max_adult_occupancy),
						max_child_occupancy: Number(roomType.max_child_occupancy),
						no_of_rooms: Number(roomType.no_of_rooms),
						includes_all_meal_plans: true,
						thumb_image_url: imageUrls[index],
						amenities: roomType.amenities,
					}))}
					renderActions={(roomType, index) => (
						<RowActionsMenu
							itemType="Room"
							itemName={roomType.name}
							onEdit={() => setDialog({ index })}
							onDelete={() => onChange(roomTypes.filter((_, i) => i !== index))}
						/>
					)}
				/>
			</StepList>
			{dialog ? (
				<OtaRoomTypeInDialog
					open
					roomType={
						editingIndex !== undefined ? roomTypes[editingIndex] : undefined
					}
					onClose={() => setDialog(null)}
					onSubmit={(values) => {
						onChange(
							editingIndex !== undefined
								? roomTypes.map((roomType, i) =>
										i === editingIndex ? values : roomType
									)
								: [...roomTypes, values]
						)
						setDialog(null)
					}}
				/>
			) : null}
		</Stack>
	)
}

// Data urls of the (not yet uploaded) images, to preview them in the table
function useImageDataUrls(files: Array<File | undefined>) {
	const [urls, setUrls] = useState<Map<File, string>>(() => new Map())
	useEffect(() => {
		let isCancelled = false
		files.forEach((file) => {
			if (!(file instanceof File)) return
			const reader = new FileReader()
			reader.addEventListener("load", () => {
				if (isCancelled) return
				setUrls((prev) =>
					prev.get(file) === reader.result
						? prev
						: new Map(prev).set(file, reader.result as string)
				)
			})
			reader.readAsDataURL(file)
		})
		return () => {
			isCancelled = true
		}
	}, [files])
	return files.map((file) => (file ? urls.get(file) : undefined))
}

/* Step 3: Meal plans, only kept in the form until the hotel is added */

function MealPlansStep({
	mealPlans,
	onChange,
}: {
	mealPlans: Array<TOtaMealPlanPayload>
	onChange: (mealPlans: Array<TOtaMealPlanPayload>) => void
}) {
	// `index` is set when editing an added meal plan
	const [dialog, setDialog] = useState<{ index?: number } | null>(null)
	const editingIndex = dialog?.index
	return (
		<Stack gap="4">
			<StepList
				title="Meal Plans"
				itemLabel="Meal Plan"
				addLabel="Add Meal Plan"
				icon={<Icons.Utensils size="32" color="subtle" />}
				isEmpty={!mealPlans.length}
				onAdd={() => setDialog({})}
			>
				<OtaMealPlansTable
					mealPlans={mealPlans}
					renderActions={(mealPlan, index) => (
						<RowActionsMenu
							itemType="Meal Plan"
							itemName={mealPlan.name}
							onEdit={() => setDialog({ index })}
							onDelete={() => onChange(mealPlans.filter((_, i) => i !== index))}
						/>
					)}
				/>
			</StepList>
			{dialog ? (
				<OtaMealPlanInDialog
					open
					mealPlan={
						editingIndex !== undefined ? mealPlans[editingIndex] : undefined
					}
					onClose={() => setDialog(null)}
					onSubmit={(payload) => {
						onChange(
							editingIndex !== undefined
								? mealPlans.map((mealPlan, i) =>
										i === editingIndex ? payload : mealPlan
									)
								: [...mealPlans, payload]
						)
						setDialog(null)
					}}
				/>
			) : null}
		</Stack>
	)
}

/* Step 4: Channel manager */

// The child ages mappings belong to the channel manager, so picking another one
// (or none) clears them
function ChannelManagerSelectResettingMappings(
	props: React.ComponentProps<typeof ChannelManagerSelect>
) {
	const form = useForm<TNewHotelFormValues>()
	return (
		<ChannelManagerSelect
			{...props}
			onChange={(value, name) => {
				form.batch(() => {
					props.onChange?.(value, name)
					form.change(
						"child_ages",
						withoutChildAgeRemotePaxTypes(form.getState().values.child_ages)
					)
				})
			}}
		/>
	)
}

function ChannelManagerStep({ name }: { name: string }) {
	return (
		<Stack gap="4">
			<Inline alignItems="center" gap="2">
				<Heading as="h2" fontSize="xl">
					Connect Channel Manager
				</Heading>
				<Text color="muted">(Optional)</Text>
			</Inline>
			<Box
				bgColor="default"
				borderWidth="1"
				rounded="md"
				padding="4"
				width="full"
				style={{ maxWidth: "36rem" }}
			>
				<SelectField
					select={ChannelManagerSelectResettingMappings}
					name={name}
					label="Connect to"
					fetchOnMount
				/>
			</Box>
		</Stack>
	)
}

/* Step 5: Child ages */

function ChildAgesStep({
	name,
	onRemotePaxTypes,
}: {
	name: string
	onRemotePaxTypes: (remotePaxTypes?: THotelRemotePaxTypes) => void
}) {
	const { value: channelManager } =
		useFieldValue<TNewHotelFormValues["channel_manager"]>("channel_manager")
	const { remotePaxTypes, isFetching, error } = useHotelRemotePaxTypes(
		channelManager?.id
	)
	// a failed fetch leaves it empty, so the hotel is still added unmapped
	useEffect(() => {
		onRemotePaxTypes(remotePaxTypes)
	}, [remotePaxTypes, onRemotePaxTypes])
	return (
		<Stack gap="4" alignItems="start">
			<Heading as="h2" fontSize="xl">
				Child Ages
			</Heading>
			{isFetching ? (
				<Text color="muted">
					<Icons.Spinner /> Loading {channelManager?.name} pax types...
				</Text>
			) : null}
			{error ? (
				<Text color="danger">
					Could not load the pax types of {channelManager?.name}. Child ages can
					be mapped later from the hotel.
				</Text>
			) : null}
			<Box style={{ maxWidth: "54rem" }} width="full">
				<ChildAgesFields name={name} remotePaxTypes={remotePaxTypes} />
			</Box>
		</Stack>
	)
}

function StepCard({ children }: { children: React.ReactNode }) {
	return (
		<Box
			bgColor="default"
			borderWidth="1"
			rounded="md"
			padding={{ xs: "4", md: "6" }}
		>
			{children}
		</Box>
	)
}

function StepSection({
	title,
	description,
	children,
}: {
	title: string
	description: string
	children: React.ReactNode
}) {
	return (
		<Stack gap="4">
			<Stack gap="1">
				<Heading as="h3" fontSize="lg">
					{title}
				</Heading>
				<Text color="muted">{description}</Text>
			</Stack>
			{children}
		</Stack>
	)
}

const bandGridStyle = {
	display: "grid",
	gridTemplateColumns: "6rem 6rem 10rem 1fr auto",
	gap: "1rem",
	alignItems: "start",
}

/**
 * Children age range and its age bands. Pass `name` when the child ages values
 * are nested under a key of the form, instead of being the form's root values.
 */
export function ChildAgesFields({
	name,
	canManage = true,
	headerAction,
	remotePaxTypes,
}: {
	name?: string
	canManage?: boolean
	headerAction?: ReactNode
	// pass for a hotel on a channel manager, to map its child ages to the channel manager's
	remotePaxTypes?: THotelRemotePaxTypes
}) {
	const prefix = name ? `${name}.` : ""
	return (
		<Stack gap="4" bgColor="default" borderWidth="1" rounded="md" padding="4">
			<Stack gap="2">
				<Inline justifyContent="between" alignItems="center">
					<Text fontWeight="semibold">Children Age range</Text>
					{headerAction}
				</Inline>
				<Inline gap="4">
					<AgeInputField
						name={`${prefix}min_age`}
						title="Min Age"
						disabled={!canManage}
						width="20"
					/>
					<AgeInputField
						name={`${prefix}max_age`}
						title="Max Age"
						disabled={!canManage}
						width="20"
					/>
				</Inline>
				<AgeRangeHelp prefix={prefix} />
			</Stack>
			<ChildAgeBands
				name={name}
				canManage={canManage}
				remotePaxTypes={remotePaxTypes}
			/>
		</Stack>
	)
}

function AgeInputField({
	name,
	title,
	disabled,
	width,
}: {
	name: string
	title: string
	disabled: boolean
	width?: BoxProps["width"]
}) {
	return (
		<TextInputField
			name={name}
			type="number"
			min={1}
			title={title}
			disabled={disabled}
			className={noSpinnersClassName}
			width={width}
			max={17}
		/>
	)
}

function AgeRangeHelp({ prefix }: { prefix: string }) {
	return (
		<GetFieldValue<number | undefined> name={`${prefix}min_age`}>
			{({ value: minAge }) => (
				<GetFieldValue<number | undefined> name={`${prefix}max_age`}>
					{({ value: maxAge }) =>
						isWholeNumber(minAge) &&
						isWholeNumber(maxAge) &&
						maxAge >= minAge ? (
							<Box
								as="ul"
								fontSize="sm"
								color="muted"
								paddingLeft="4"
								style={{ listStyleType: "disc" }}
							>
								<li>
									Below <b>{minAge}y</b>, children are considered{" "}
									<b>complementary</b>
								</li>
								<li>
									From <b>{maxAge + 1}y</b>, children are considered as{" "}
									<b>adult</b>
								</li>
								<li>
									A child of age <b>{maxAge}y 11m</b> is considered as{" "}
									<b>{maxAge}y child</b>
								</li>
							</Box>
						) : null
					}
				</GetFieldValue>
			)}
		</GetFieldValue>
	)
}

function ChildAgeBands({
	name,
	canManage,
	remotePaxTypes,
}: {
	name?: string
	canManage: boolean
	remotePaxTypes?: THotelRemotePaxTypes
}) {
	const prefix = name ? `${name}.` : ""
	const bandsName = `${prefix}bands`
	const form = useForm()
	const { value: minAge } = useFieldValue<TChildAgesFormValues["min_age"]>(
		`${prefix}min_age`
	)
	const { value: maxAge } = useFieldValue<TChildAgesFormValues["max_age"]>(
		`${prefix}max_age`
	)
	const { value: bandsValue } =
		useFieldValue<TChildAgesFormValues["bands"]>(bandsName)
	const { value: awebRemotePaxType } = useFieldValue<
		TChildAgesFormValues["aweb_remote_pax_type"]
	>(`${prefix}aweb_remote_pax_type`)
	const bands = bandsValue || []
	const values = { min_age: minAge, max_age: maxAge, bands }
	const startAges = bandStartAges(values.min_age, bands)
	const lastEndAge = bands[bands.length - 1]?.end_age
	const canAddBand = !(
		isWholeNumber(lastEndAge) &&
		isWholeNumber(values.max_age) &&
		lastEndAge >= values.max_age
	)

	function mapRemotePaxTypes(mapping: IChildAgeRemotePaxTypesFormSchema) {
		if (!remotePaxTypes) return
		// the form holds the resolved pax type, so an unmapped one is the default
		form.batch(() => {
			mapping.bands.forEach((band, index) => {
				for (const paxType of occupancyPaxTypes(bands[index]?.occupancy)) {
					const field = bandRemotePaxTypeField(paxType)
					form.change(
						`${bandsName}[${index}].${field}`,
						band[field] || remotePaxTypes.default_child_remote_pax_type
					)
				}
			})
			form.change(
				`${prefix}aweb_remote_pax_type`,
				mapping.aweb_remote_pax_type ||
					remotePaxTypes.default_adult_remote_pax_type
			)
		})
	}

	return (
		<FieldArray<TChildAgeBand> name={bandsName}>
			{({ fields }) => (
				<Stack gap="3" alignItems="start">
					{bands.length > 0 ? (
						<Box style={bandGridStyle} fontWeight="semibold">
							<Text>Start Age</Text>
							<Text>End Age</Text>
							<Text>Occupancy</Text>
						</Box>
					) : null}
					{fields.map((fieldName, index) => {
						const start = startAges[index]
						const band = bands[index] || {}
						return (
							<Box key={fieldName} style={{ ...bandGridStyle, width: "100%" }}>
								<Input
									value={start ?? ""}
									disabled
									readOnly
									title="Start Age"
								/>
								<AgeInputField
									name={`${fieldName}.end_age`}
									title="End Age"
									disabled={!canManage}
								/>
								<SelectInputField
									name={`${fieldName}.occupancy`}
									disabled={!canManage}
								>
									{CHILD_AGE_BAND_OCCUPANCIES.map((occupancy) => (
										<option key={occupancy.value} value={occupancy.value}>
											{occupancy.label}
										</option>
									))}
								</SelectInputField>
								<Inline gap="2" whiteSpace="nowrap" paddingY="2">
									{isWholeNumber(start) && isWholeNumber(band.end_age)
										? occupancyPaxTypes(band.occupancy).map((paxType) => (
												<ChildAgePaxBadge
													key={paxType}
													paxType={paxType}
													label={`${paxType} (${start}-${band.end_age})`}
													remotePaxType={
														remotePaxTypes
															? bandRemotePaxType(band, paxType, remotePaxTypes)
															: undefined
													}
												/>
											))
										: null}
								</Inline>
								{canManage && bands.length > 1 ? (
									<Button
										type="button"
										level="tertiary"
										size="sm"
										title="Remove Age Band"
										onClick={() => fields.remove(index)}
									>
										<Icons.Cancel />
									</Button>
								) : (
									<span />
								)}
							</Box>
						)
					})}
					{canManage ? (
						<Button
							type="button"
							level="secondary"
							disabled={!canAddBand}
							onClick={() =>
								fields.push({
									end_age: values.max_age,
									occupancy: "CNB",
								})
							}
						>
							Add Age Band
						</Button>
					) : null}

					{remotePaxTypes ? (
						<>
							<Divider width="full" marginY="0" />
							<Box style={{ ...bandGridStyle, width: "100%" }}>
								<Input
									style={{ gridColumn: "span 2" }}
									value={
										isWholeNumber(values.max_age)
											? `${values.max_age + 1} and above`
											: ""
									}
									disabled
									readOnly
									title="Adult Age"
								/>
								<Input value="Adult" disabled readOnly title="Adult" />
								<Inline gap="2" whiteSpace="nowrap" paddingY="2">
									<ChildAgePaxBadge
										paxType="AWEB"
										label="AWEB"
										remotePaxType={
											awebRemotePaxType ||
											remotePaxTypes.default_adult_remote_pax_type
										}
									/>
								</Inline>
							</Box>
							{canManage ? (
								<MapChildAgeRemotePaxTypesInDialog
									bands={bands}
									startAges={startAges}
									awebRemotePaxType={awebRemotePaxType}
									remotePaxTypes={remotePaxTypes}
									onSuccess={mapRemotePaxTypes}
								>
									{({ open }) => (
										<Button type="button" level="secondary" onClick={open}>
											<Icons.Pencil /> Map Channel Manager
										</Button>
									)}
								</MapChildAgeRemotePaxTypesInDialog>
							) : null}
						</>
					) : null}
				</Stack>
			)}
		</FieldArray>
	)
}

// The pax type's pill, with the channel manager's pax type it is sold as under it
function ChildAgePaxBadge({
	paxType,
	label,
	remotePaxType,
}: {
	paxType: THotelOtaChildAgePaxType | "AWEB"
	label: string
	remotePaxType?: string | null
}) {
	return (
		<Stack gap="1" alignItems="start">
			<Box
				paddingX="2"
				paddingY="1"
				rounded="md"
				style={COLUMN_PILL_COLORS[columnPillVariant({ paxType })]}
				fontSize="sm"
				fontWeight="semibold"
			>
				{label}
			</Box>
			{remotePaxType ? (
				<Text fontSize="xs" fontWeight="semibold" color="success">
					{remotePaxType}
				</Text>
			) : null}
		</Stack>
	)
}

export interface IChildAgeRemotePaxTypesFormSchema {
	aweb_remote_pax_type?: string
	bands: Array<
		Pick<TChildAgeBand, "cnb_remote_pax_type" | "cweb_remote_pax_type">
	>
}

type TChildAgeRemotePaxTypesProps = {
	bands: Array<TChildAgeBand>
	// the start age of each band, see `bandStartAges`
	startAges: Array<number | undefined>
	remotePaxTypes: THotelRemotePaxTypes
}

function ChildAgeRemotePaxTypesForm({
	bands,
	startAges,
	remotePaxTypes,
	initialValues,
	onSubmit,
}: TChildAgeRemotePaxTypesProps & {
	initialValues: IChildAgeRemotePaxTypesFormSchema
	onSubmit: (values: IChildAgeRemotePaxTypesFormSchema) => Promise<void> | void
}) {
	const remotePaxTypeOptions = remotePaxTypes.remote_pax_types.map(
		(remotePaxType) => (
			<option key={remotePaxType} value={remotePaxType}>
				{remotePaxType}
			</option>
		)
	)
	return (
		<Form<IChildAgeRemotePaxTypesFormSchema>
			initialValues={initialValues}
			onSubmit={onSubmit}
			subscription={{ submitting: true, pristine: true }}
		>
			{({ handleSubmit, submitting, pristine }) => (
				<form
					noValidate
					onSubmit={(event) => {
						// the dialog is rendered inside the child ages form, and React
						// bubbles its submit event through the portal to that form
						event.stopPropagation()
						void handleSubmit(event)
					}}
				>
					<Stack gap="4">
						<Stack gap="3" paddingX="4" paddingTop="4">
							{bands.map((band, index) => {
								const start = startAges[index]
								if (!isWholeNumber(start) || !isWholeNumber(band.end_age)) {
									return null
								}
								return occupancyPaxTypes(band.occupancy).map((paxType) => (
									<ChildAgeRemotePaxTypeRow
										key={`${index}-${paxType}`}
										paxType={paxType}
										label={`${paxType} (${start}-${band.end_age})`}
									>
										<SelectInputField
											name={`bands[${index}].${bandRemotePaxTypeField(paxType)}`}
										>
											<option value="">Select</option>
											{remotePaxTypeOptions}
										</SelectInputField>
									</ChildAgeRemotePaxTypeRow>
								))
							})}
							<Divider marginY="0" />
							<ChildAgeRemotePaxTypeRow paxType="AWEB" label="AWEB">
								<SelectInputField name="aweb_remote_pax_type">
									<option value="">Select</option>
									{remotePaxTypeOptions}
								</SelectInputField>
							</ChildAgeRemotePaxTypeRow>
						</Stack>
						<Divider marginY="0" />
						<Inline padding="4" paddingTop="0" justifyContent="end">
							<Button
								type="submit"
								level="primary"
								disabled={pristine || submitting}
							>
								{submitting ? <Icons.Spinner /> : null}{" "}
								{submitting ? "Mapping..." : "Map"}
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

function ChildAgeRemotePaxTypeRow({
	paxType,
	label,
	children,
}: {
	paxType: THotelOtaChildAgePaxType | "AWEB"
	label: string
	children: ReactNode
}) {
	return (
		<Box
			style={{
				display: "grid",
				gridTemplateColumns: "10rem 2rem 1fr",
				alignItems: "center",
			}}
		>
			<Inline>
				<ChildAgePaxBadge paxType={paxType} label={label} />
			</Inline>
			<Text>→</Text>
			{children}
		</Box>
	)
}

/**
 * Maps the child age bands and the extra bed to the pax types of the hotel's
 * channel manager. Doesn't save, it hands the mapping to `onSuccess`.
 */
export function MapChildAgeRemotePaxTypesInDialog({
	bands,
	startAges,
	awebRemotePaxType,
	remotePaxTypes,
	onSuccess,
	children,
}: TChildAgeRemotePaxTypesProps & {
	awebRemotePaxType: string | undefined
	onSuccess: (values: IChildAgeRemotePaxTypesFormSchema) => void
	children: (props: { open: () => void }) => ReactNode
}) {
	const [isOpen, open, close] = useDialog()
	// shows what each pax type is currently sold as, the default when unmapped
	const initialValues = useMemo(
		(): IChildAgeRemotePaxTypesFormSchema => ({
			aweb_remote_pax_type:
				awebRemotePaxType ||
				remotePaxTypes.default_adult_remote_pax_type ||
				undefined,
			bands: bands.map((band) => ({
				cnb_remote_pax_type:
					bandRemotePaxType(band, "CNB", remotePaxTypes) || undefined,
				cweb_remote_pax_type:
					bandRemotePaxType(band, "CWEB", remotePaxTypes) || undefined,
			})),
		}),
		[bands, awebRemotePaxType, remotePaxTypes]
	)
	return (
		<>
			{children({ open })}
			<Dialog
				open={isOpen}
				onClose={close}
				title="Channel Manager - Child Age Band Mapping"
				sm
			>
				<Dialog.Body paddingX="0" paddingY="0">
					<ChildAgeRemotePaxTypesForm
						bands={bands}
						startAges={startAges}
						remotePaxTypes={remotePaxTypes}
						initialValues={initialValues}
						onSubmit={(values) => {
							onSuccess(values)
							close()
						}}
					/>
				</Dialog.Body>
			</Dialog>
		</>
	)
}
